import { Box, Container, Grid, makeStyles, Typography } from '@material-ui/core'
import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import {
  dictionariesVar,
  projectVar,
  selectedVersionsVar,
} from '../../../apollo/reactive-vars'
import { VersionCostCalculator } from '../../../calculators/costs/VersionCostCalculator'
import {
  PricingModelFragment,
  ProjectVersionFragment,
} from '../../../generated/graphql'
import { Section } from '../../common/Section'
import { PricingModelReport } from '../../pricing-models/PricingModelReport'
import { CostBreakdownBarChart } from '../cost-charts/CostBreakdownBarChart'
import { CumulativeCostChart } from '../cost-charts/CumulativeCostChart'
import { TotalCostsTable } from '../cost-tables/TotalCostsTable'
import CostsBreakdownReport from './CostsBreakdownReport'
import { ReportHeader } from '../../common/ReportHeader'
import { PageBreak } from '../../common/PageBreak'
import { ReportVersionsDivider } from './ReportVersionsDivider'

type Props = {
  versionCostCalculators: VersionCostCalculator[]
}

const useStyles = makeStyles({
  printContainer: {
    width: '1000px',
  },
})

export type PricingModelWithVersions = PricingModelFragment & {
  usedInVersions: ProjectVersionFragment[]
}

export const CostsReport = forwardRef<HTMLDivElement, Props>(
  (props, ref): React.ReactElement => {
    const { versionCostCalculators } = props
    const project = projectVar()
    const classes = useStyles()
    const { containerIdsToNames } = useReactiveVar(dictionariesVar)
    const selectedVersions = useReactiveVar(selectedVersionsVar)
    const { t } = useTranslation()

    if (!project || selectedVersions.length !== versionCostCalculators.length) {
      return <></>
    }

    const selectedVersionIds = versionCostCalculators.map((version) =>
      version.getId(),
    )

    const pricingModels = project.pricingModels.reduce(
      (filtered, pricingModel) => {
        const pricingModelVersions = project.versions
          .filter((version) => selectedVersionIds.includes(version.id))
          .filter((version) => version.pricingModel?.id === pricingModel.id)

        return pricingModelVersions.length > 0
          ? [
              ...filtered,
              { ...pricingModel, usedInVersions: pricingModelVersions },
            ]
          : filtered
      },
      [] as PricingModelWithVersions[],
    )

    return (
      <Container className={classes.printContainer} ref={ref}>
        <ReportHeader
          title={project?.name ?? ''}
          subtitle1={t('costs.common.lifecycleCosts')}
          subtitle2={t('common.results')}
        />
        <Section title={t('costs.versions')} printPadding>
          {versionCostCalculators.map((version) => (
            <Box key={version.getId()}>{version.getName()}</Box>
          ))}
        </Section>

        <ReportVersionsDivider
          versions={versionCostCalculators}
          component={TotalCostsTable}
          componentProps={{
            selectedContainerName: null,
            containerIdsToNames,
            selectedVersions,
            comparisonVersion: versionCostCalculators[0],
          }}
        />

        <PageBreak />

        <ReportHeader
          title={project?.name ?? ''}
          subtitle1={t('costs.common.lifecycleCosts')}
          subtitle2={t('common.results')}
        />

        <CumulativeCostChart
          versions={versionCostCalculators}
          selectedContainerName={null}
          containerIdsToNames={containerIdsToNames}
          printPadding
        />

        <CostBreakdownBarChart
          versions={versionCostCalculators}
          selectedContainerName={null}
          containerIdsToNames={containerIdsToNames}
          printPadding
        />

        <PageBreak />

        <ReportHeader
          title={project?.name ?? ''}
          subtitle1={t('costs.common.lifecycleCosts')}
          subtitle2={t('common.basicInfo')}
        />

        <Grid container spacing={2} justifyContent="space-evenly">
          {pricingModels.map((pricingModel) => (
            <Grid item key={pricingModel.id}>
              <Box display="table">
                <PricingModelReport pricingModel={pricingModel} />
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box justifyContent="center">
          <Typography variant="caption" align="center" component="div">
            {`* ${t('pricingModels.energyPriceTotalValueTooltip')}`}
          </Typography>
        </Box>

        <PageBreak />

        <ReportHeader
          title={project?.name ?? ''}
          subtitle1={t('costs.common.lifecycleCosts')}
          subtitle2={t('costs.common.breakdown')}
        />
        <CostsBreakdownReport />
      </Container>
    )
  },
)
